import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { ButtonComponent, StatusIconComponent } from '@mode/capra';
import { DialogHeaderComponent, StatusBannerComponent, ToggleSwitchComponent } from '@mode/shared/ui';
import { SharedUtilSegmentModule } from '@mode/shared/util-segment';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PromoteDatasetForm } from './promote-dataset-form';
import { Dataset, DatasetFacade } from '@mode/dataset/contract';
import { EnhancedDatasetResult, ProducerType, ProducerTypes } from '@mode/shared/contract-common';
import { combineLatestWith, map, take } from 'rxjs/operators';
import { DatasetMetadataComponent } from '@mode/dataset/ui';
import { DatasourceFacade } from '@mode/accounts/domain';
import { Observable, of } from 'rxjs';
import { DataViewFacade, SwitchViewFacade } from '@mode/switch-views/contract';
import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';

@Component({
  selector: 'mode-republish-dataset-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    StatusIconComponent,
    DialogHeaderComponent,
    StatusBannerComponent,
    SharedUtilSegmentModule,
    FormsModule,
    ReactiveFormsModule,
    DatasetMetadataComponent,
    ToggleSwitchComponent,
  ],
  templateUrl: './promote-dataset-dialog.component.html',
  styleUrls: ['./promote-dataset-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoteDatasetDialogComponent implements OnInit {
  switchViewToken: string;
  form: PromoteDatasetForm;
  shouldAutoPublish = false;
  datasourceDisplayName$: Observable<string> = of('');
  hasCalcFields$: Observable<boolean> = of(false);
  isNotebookCell$: Observable<boolean> = of(false);
  dataset$: Observable<Dataset | null> = of(null);
  enhancedDatasetResult$: Observable<EnhancedDatasetResult | undefined> = of(undefined);
  readonly isAnalystStudio = this.thoughtspotIntegrationService.currOrgIntegrated();

  @Output() promoteDatasetEvent = new EventEmitter<{
    newDatasetName: string;
    queryToken: string;
    reportToken: string;
    shouldAutoPublish: boolean;
  }>();

  constructor(
    protected dialogRef: MatLegacyDialogRef<PromoteDatasetDialogComponent>,
    private datasetFacade: DatasetFacade,
    private dataViewFacade: DataViewFacade,
    private datasourceFacade: DatasourceFacade,
    private switchViewFacade: SwitchViewFacade,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService,
    @Inject(MAT_LEGACY_DIALOG_DATA)
    private data: {
      switchViewToken: string;
    }
  ) {
    this.switchViewToken = data.switchViewToken;
    this.form = new PromoteDatasetForm();
  }

  ngOnInit(): void {
    this.enhancedDatasetResult$ = this.switchViewFacade.getEnhancedDataseResult(this.switchViewToken);
    this.dataset$ = this.datasetFacade.dataset$;

    this.enhancedDatasetResult$.pipe(take(1)).subscribe({
      next: (dataset) => {
        this.isNotebookCell$ = of(dataset?.producerType === ProducerTypes.NotebookCell);

        this.form.updateName(dataset?.queryName ?? '');
      },
    });

    this.hasCalcFields$ = this.dataViewFacade.isCalcBySwitchViewTokenFromSwitchViewAttr(this.switchViewToken);

    this.datasourceDisplayName$ = this.datasourceFacade.datasources$.pipe(
      combineLatestWith(this.enhancedDatasetResult$),
      map(([datasources, dataset]) => {
        if (dataset?.producerType === ProducerTypes.GoogleSheet) return 'Google Sheets';
        return datasources.find((ds) => ds.id === dataset?.datasourceId)?.displayName ?? '';
      })
    );
  }

  promoteDataset(): void {
    if (this.form.valid) {
      this.enhancedDatasetResult$.pipe(take(1)).subscribe({
        next: (dataset) => {
          if (!dataset) {
            console.error('Dataset not found');
            return;
          }
          this.promoteDatasetEvent.emit({
            newDatasetName: this.form.name.value,
            queryToken: dataset.queryToken,
            reportToken: dataset.datasetToken,
            shouldAutoPublish: this.shouldAutoPublish,
          });
        },
      });
      this.close();
    } else {
      this.form.markAllAsTouched();
    }
  }

  toggleShouldAutoPublish(shouldAutoPublish: boolean) {
    this.shouldAutoPublish = shouldAutoPublish;
  }

  close() {
    this.dialogRef.close();
  }
}
